import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BookOpen, Plus, Trash2, Search, Filter, Tag, ChevronRight, AlertCircle, Check, X, Loader2
} from 'lucide-react';
import { brandColors } from '../components/Brand';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_MAP = {
  core_business: { label: 'Core Business', color: '#1e3a5f' },
  retail: { label: 'Retail', color: '#7c3aed' },
  supply_chain: { label: 'Supply Chain', color: '#0891b2' },
  finance: { label: 'Financial Terms', color: '#059669' },
  analytics: { label: 'Analytics', color: '#d97706' },
  marketing: { label: 'Marketing', color: '#dc2626' },
  platform: { label: 'Platform / Technical', color: '#6366f1' },
  custom: { label: 'Custom', color: '#e85d04' },
};

const TerminologyAdmin = () => {
  const { getAuthHeaders } = useAuth();
  const [terms, setTerms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [newTerm, setNewTerm] = useState('');
  const [newCategory, setNewCategory] = useState('custom');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    document.title = 'ReasonDMS | Terminology Admin';
    loadTerms();
  }, []);

  const loadTerms = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/multilingual/terminology`, { headers });
      const data = await res.json();
      if (data.success) {
        setTerms(data.terms || []);
        setCategories(data.categories || []);
      }
    } catch (err) {
      toast.error('Failed to load terminology');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newTerm.trim()) { toast.error('Enter a term'); return; }
    setAdding(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API_URL}/api/multilingual/terminology`, {
        method: 'POST', headers,
        body: JSON.stringify({ term: newTerm.trim(), category: newCategory })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Added "${data.term}"`);
        setNewTerm('');
        setShowAddForm(false);
        loadTerms();
      } else {
        toast.error(data.error || 'Failed to add term');
      }
    } catch (err) {
      toast.error('Failed to add term');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (term) => {
    setDeleting(term);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/multilingual/terminology/${encodeURIComponent(term)}`, {
        method: 'DELETE', headers
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Removed "${term}"`);
        loadTerms();
      } else {
        toast.error('Cannot delete system terms');
      }
    } catch (err) {
      toast.error(err.message?.includes('404') ? 'System terms cannot be deleted' : 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const filteredTerms = useMemo(() => {
    let result = terms;
    if (filterCategory !== 'all') result = result.filter(t => t.category === filterCategory);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t => t.term.toLowerCase().includes(q));
    }
    return result;
  }, [terms, filterCategory, searchQuery]);

  const categoryStats = useMemo(() => {
    const stats = {};
    terms.forEach(t => { stats[t.category] = (stats[t.category] || 0) + 1; });
    return stats;
  }, [terms]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-transparent mx-auto" style={{ borderTopColor: brandColors.primary, borderRightColor: brandColors.primary }} />
          <p className="mt-4 text-sm text-slate-500">Loading terminology...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="terminology-admin-page" className="space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: brandColors.primary, fontFamily: 'var(--font-heading)' }}>
            Protected Business Terminology
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Manage terms that must remain in English across all translations ({terms.length} terms)
          </p>
        </div>
        <Button
          data-testid="add-term-toggle-btn"
          onClick={() => setShowAddForm(!showAddForm)}
          className="shadow-sm hover:-translate-y-0.5 transition-all duration-200 text-sm"
          style={{ backgroundColor: brandColors.accent }}
        >
          {showAddForm ? <X className="w-4 h-4 mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />}
          {showAddForm ? 'Cancel' : 'Add Term'}
        </Button>
      </motion.div>

      {/* Add Term Form */}
      {showAddForm && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
          <Card className="border border-slate-200 bg-white" data-testid="add-term-form">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Term</label>
                  <Input
                    data-testid="new-term-input"
                    value={newTerm}
                    onChange={(e) => setNewTerm(e.target.value)}
                    placeholder="e.g., Invoice, Purchase Order, GST"
                    className="h-10 border-slate-200"
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  />
                </div>
                <div className="w-full sm:w-48">
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Category</label>
                  <Select value={newCategory} onValueChange={setNewCategory}>
                    <SelectTrigger className="h-10 border-slate-200" data-testid="new-term-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_MAP).map(([key, val]) => (
                        <SelectItem key={key} value={key}>{val.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    data-testid="add-term-submit-btn"
                    onClick={handleAdd}
                    disabled={adding || !newTerm.trim()}
                    className="h-10 px-6 text-sm"
                    style={{ backgroundColor: brandColors.primary }}
                  >
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Add</>}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Custom terms are tenant-specific. System terms cannot be modified.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Category Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <button
          data-testid="filter-all"
          onClick={() => setFilterCategory('all')}
          className={`p-2.5 rounded-lg border text-center transition-all duration-200 ${filterCategory === 'all' ? 'bg-slate-800 border-slate-800 text-white shadow-md' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
        >
          <p className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{terms.length}</p>
          <p className="text-[10px] font-medium uppercase tracking-wider">All</p>
        </button>
        {Object.entries(CATEGORY_MAP).map(([key, val]) => (
          <button
            key={key}
            data-testid={`filter-${key}`}
            onClick={() => setFilterCategory(filterCategory === key ? 'all' : key)}
            className={`p-2.5 rounded-lg border text-center transition-all duration-200 ${filterCategory === key ? 'shadow-md text-white' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
            style={filterCategory === key ? { backgroundColor: val.color, borderColor: val.color } : {}}
          >
            <p className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{categoryStats[key] || 0}</p>
            <p className="text-[10px] font-medium uppercase tracking-wider truncate">{val.label}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="term-search-input"
          placeholder="Search terms..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-10 border-slate-200 bg-white"
        />
      </div>

      {/* Terms List */}
      <Card className="border border-slate-200 bg-white">
        <CardHeader className="pb-2 pt-4 px-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 rounded-full" style={{ backgroundColor: brandColors.primary }} />
              <CardTitle className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'var(--font-heading)' }}>
                {filterCategory !== 'all' ? `${CATEGORY_MAP[filterCategory]?.label || filterCategory} Terms` : 'All Protected Terms'}
              </CardTitle>
            </div>
            <span className="text-xs text-slate-400">{filteredTerms.length} terms</span>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5">
          {filteredTerms.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium text-slate-500">No terms found</p>
              <p className="text-xs text-slate-400 mt-1">
                {searchQuery ? 'Try a different search query' : 'Add your first custom term'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
              {filteredTerms.map((term, idx) => {
                const cat = CATEGORY_MAP[term.category] || { label: term.category, color: '#6b7280' };
                const isCustom = !['core_business', 'retail', 'supply_chain', 'finance', 'analytics', 'marketing', 'platform'].some(
                  c => c === term.category && terms.indexOf(term) < 97
                );
                return (
                  <div
                    key={`${term.term}-${idx}`}
                    data-testid={`term-item-${idx}`}
                    className="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 transition-colors group border border-transparent hover:border-slate-100"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: cat.color }} />
                      <span className="text-sm font-medium text-slate-800 truncate">{term.term}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-medium shrink-0 hidden sm:inline">
                        {cat.label}
                      </span>
                    </div>
                    {term.category === 'custom' && (
                      <Button
                        data-testid={`delete-term-${idx}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(term.term)}
                        disabled={deleting === term.term}
                        className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 hover:bg-red-50"
                      >
                        {deleting === term.term ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TerminologyAdmin;
